import { Loader2 } from 'lucide-react';

/**
 * "What am I actually starting?" — ROUTE_INVENTORY §2.11 #38.
 *
 * Three states, and the third is the one that earns the component: a session
 * whose size is the LENGTH CAP rather than the match count carries a caveat
 * saying so. Without it the wizard silently practices 20 of 60 matching
 * concepts and reports "20 concepts will be practiced", which is true and
 * misleading in the same breath.
 *
 * Zero is styled as a warning rather than an error — nothing has gone wrong,
 * the filters are just too narrow, and Start is disabled behind it.
 */
export function SessionSizeSummary({
  effectiveSessionSize,
  length,
  isCapped,
  isLoading,
}: {
  effectiveSessionSize: number | null;
  length: number | 'all';
  isCapped: boolean;
  isLoading: boolean;
}) {
  // Measured on this block's own rose tint over the modal surface:
  // `text-rose-600` came to 4.00:1 and the faded `text-rose-700/80` to
  // 4.16:1. Both are families this route keeps tripping over — the -600 step,
  // and subordinating by alpha.
  const zero = effectiveSessionSize === 0;
  return (
    <div
      className={`flex items-center justify-between gap-3 rounded-lg border p-4 ${
        zero
          ? 'border-rose-200 bg-rose-50/60 dark:border-rose-900/60 dark:bg-rose-900/20'
          : 'border-border bg-muted/50 dark:bg-white/[0.03]'
      }`}
    >
      <div className='min-w-0 flex-1'>
        <div
          className={`text-xs font-semibold ${
            zero ? 'text-rose-700 dark:text-rose-400' : 'text-muted-foreground'
          }`}
        >
          Session size
        </div>
        {isLoading || effectiveSessionSize == null ? (
          <div className='mt-1 flex items-center gap-2 text-sm text-muted-foreground'>
            <Loader2 className='h-3.5 w-3.5 animate-spin' />
            Counting matches…
          </div>
        ) : zero ? (
          <div className='mt-1 flex items-baseline gap-1.5'>
            <span className='text-2xl font-semibold tabular-nums text-rose-700 dark:text-rose-300'>
              0
            </span>
            <span className='text-sm text-rose-700 dark:text-rose-300'>
              matches - adjust filters
            </span>
          </div>
        ) : (
          <div className='mt-1'>
            <div className='flex items-baseline gap-1.5'>
              <span className='text-2xl font-semibold tabular-nums text-foreground'>
                {effectiveSessionSize}
              </span>
              <span className='text-sm text-muted-foreground'>
                concept{effectiveSessionSize === 1 ? '' : 's'} will be practiced
              </span>
            </div>
            {length !== 'all' &&
              isCapped &&
              effectiveSessionSize === length && (
                <p className='mt-0.5 text-xs text-muted-foreground'>
                  Capped by your length pick. More concepts match - increase
                  Length to practice all of them.
                </p>
              )}
          </div>
        )}
      </div>
    </div>
  );
}
